<script setup>
    import headerDefault from "@/components/App/header-default/header-default.vue";
    import footerDefault from "@/components/App/footer/footer.vue";
    import ticker from "@/components/App/ticker.vue";
    import axios from "axios";

    import { renderToString } from 'vue/server-renderer';

    import {ref, onMounted, onUpdated, nextTick, inject, watch, useTemplateRef, h} from 'vue';
    import { useStore } from 'vuex';
    import { useCommonComposable } from '@/composables/commonComposable.js';

    import { useRoute, useRouter } from 'vue-router';
    import {useHead} from "@vueuse/head";

    useHead({
        title: "Авторизация — Baller | Оригинальные джерси",
        meta: [
            {name: 'description', content: "Описание страницы"}
        ],
    })

    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const notify = inject('notify');

    const emailValue = ref('')
    const passwordValue = ref('')
    const phoneValue = ref('')
    const errorText = ref('')
    const errorType = ref('')
    const authType = ref('')
    const firstnameValue = ref('')
    const lastnameValue = ref('')

    const codeValue = ref('')
    const code = ref(0);

    const registerStage = ref(1)

    const { user, getUser } = useCommonComposable();

    const hasNumbers = (str) => {
        return /\d/.test(str);
    }

    const hasSpecialChars = (str) => {
        const specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
        return specialChars.test(str);
    }

    const validateEmail = (email) => {
        if (!email.split('@')[1]) {
            errorText.value = 'Указан невалидный email. Не забудьте указать полный адрес и использовать символ @';
            errorType.value = 'no-email'
            return false
        }

        return true
    }

    const validatePassword = (password) => {
        if (password.split('').length < 8) {
            errorText.value = 'Минимальное количество символов пароля: 8';
            errorType.value = 'valid-error'
            return false
        }

        return true
    }

    const validatePhone = (phone) => {
        if (!(/^(?:\+)\d+$/.test(phone))) {
            errorText.value = 'Невалидный номер телефона. Начните с символа + и далее указывайте только числа';
            errorType.value = 'no-phone'
            return false
        }

        if (phone.split('').length < 12) {
            errorText.value = 'Недостаточное количество символов в номере телефона';
            errorType.value = 'valid-error'
            return false
        }

        return true
    }

    const validateName = (firstname, lastname) => {
        if (firstname.split('').length < 2) {
            errorText.value = 'Минимальное количество символов для поля имени: 2'
            errorType.value = 'no-firstname'
            return false
        }

        if (lastname.split('').length < 2) {
            errorText.value = 'Минимальное количество символов для поля фамилии: 2'
            errorType.value = 'no-lastname'
            return false
        }

        if (hasNumbers(firstname) || hasNumbers(lastname)) {
            errorText.value = 'В поле имени и фамилии содержатся числа'
            errorType.value = 'valid-error'
            return false
        }

        if (hasSpecialChars(firstname) || hasSpecialChars(lastname)) {
            errorText.value = 'В поле имени и фамилии содержатся спец. символы'
            errorType.value = 'valid-error'
            return false
        }

        return true
    }

    const initLogin = async () => {
        if (!emailValue.value || !passwordValue.value) {
            errorText.value = 'Не указан email или пароль';
            errorType.value = 'valid-error';
            return;
        }

        if (!validateEmail(emailValue.value) || !validatePassword(passwordValue.value)) return;

        errorText.value = '';
        errorType.value = '';

        const config = { headers: { 'Cache-Control': 'no-cache' } };
        const response = await axios.post(process.env.VUE_APP_API_URL + "/users/login", {
            email: emailValue.value,
            password: passwordValue.value,
        }, config); // делаем запрос к API

        if (response?.data.token) {
            notify('Успешная авторизация')
            saveToken(response.data.token);

            await getUser()
            await store.dispatch(`address/fetchUserAddresses`, {customerId: user.value[0].ID});

            await router.push('/products');
        }

        if (response?.data?.error) {
            notify(response.data?.error);
            errorText.value = response.data.error
            errorType.value = response.data.type
        }
    }

    const sendConfirmCode = async () => {
        if ((!validateName(firstnameValue.value, lastnameValue.value)) || !validatePhone(phoneValue.value) || !validateEmail(emailValue.value)) return;

        code.value = Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000

        const html =
            `<html>
<head>
    <style>
        table {
            border-collapse: separate;
            table-layout: fixed;
        }
        table td {
            border-collapse: collapse
        }
        .ExternalClass {
            width: 100%
        }
        .ExternalClass,
        .ExternalClass p,
        .ExternalClass span,
        .ExternalClass font,
        .ExternalClass td,
        .ExternalClass div {
            line-height: 100%
        }
        body, a, li, p, h1, h2, h3 {
            -ms-text-size-adjust: 100%;
            -webkit-text-size-adjust: 100%;
        }
        html {
            -webkit-text-size-adjust: none !important
        }
        body {
            min-width: 100%;
            margin: 0;
            padding: 0;
        }
        body, #innerTable {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale
        }
        #innerTable img+div {
            display: none !important
        }
        img {
            Margin: 0;
            padding: 0;
            -ms-interpolation-mode: bicubic
        }
        h1, h2, h3, p, a {
            line-height: inherit;
            overflow-wrap: normal;
            white-space: normal;
            word-break: break-word
        }
        a {
            text-decoration: none
        }
        h1, h2, h3, p {
            min-width: 100%!important;
            width: 100%!important;
            max-width: 100%!important;
            display: inline-block!important;
            border: 0;
            padding: 0;
            margin: 0
        }
        a[x-apple-data-detectors] {
            color: inherit !important;
            text-decoration: none !important;
            font-size: inherit !important;
            font-family: inherit !important;
            font-weight: inherit !important;
            line-height: inherit !important
        }
        u + #body a {
            color: inherit;
            text-decoration: none;
            font-size: inherit;
            font-family: inherit;
            font-weight: inherit;
            line-height: inherit;
        }
        a[href^="mailto"],
        a[href^="tel"],
        a[href^="sms"] {
            color: inherit;
            text-decoration: none
        }

        @media (min-width: 481px) {
            .hd { display: none!important }
        }

        @media (max-width: 480px) {
            .hm { display: none!important }
        }

        @media (max-width: 480px) {
            .t37,.t42{line-height:0!important;display:none!important}.t38{padding:40px!important;border-radius:0!important}.t28{text-align:left!important}.t27{vertical-align:top!important;width:auto!important;max-width:100%!important}
        }
    </style>
</head>
<body id="body" class="t45" style="min-width:100%;margin:0;padding:0;background-color:#FFFFFF;">
<div class="t44" style="background-color:#FFFFFF;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td class="t43" style="font-size:0;line-height:0;background-color:#FFFFFF;" valign="top" align="center">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" align="center" id="innerTable"><tr><td><div class="t37" style="line-height:50px;font-size:1px;display:block;"></div></td></tr><tr><td align="center">
        <table class="t41" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;"><tr><td width="600" class="t40" style="width:600px;">
            <table class="t39" role="presentation" cellpadding="0" cellspacing="0" width="100%" style="width:100%;"><tr><td class="t38" style="border:1px solid #EBEBEB;overflow:hidden;background-color:#FFFFFF;padding:44px 42px 32px 42px;border-radius:3px 3px 3px 3px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100% !important;"><tr><td align="left">
                <table class="t4" role="presentation" cellpadding="0" cellspacing="0" style="Margin-right:auto;margin-bottom: 20px"><tr><td width="42" class="t3" style="width:42px;">
                    <table class="t2" role="presentation" cellpadding="0" cellspacing="0" width="100%" style="width:100%;"><tr><td class="t1"><div style="font-size:0;"><img class="t0" style="display:block;border:0;height:auto;width:100%;Margin:0;max-width:100%;" width="42" height="42" alt="" src="https://dd4d2dd5-22a6-45c7-a084-273ca1ab208a.b-cdn.net/e/a0ebf28d-eee4-412c-af6a-4012d694d1ce/9edd498a-0a29-415f-8703-8234e1a0cbb3.png"/></div></td></tr></table>
                </td></tr></table>
            </td></tr><tr><td><div class="t5" style="line-height:42px;font-size:1px;display:block;"></div></td></tr><tr><td align="center">
                <table class="t10" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;"><tr><td width="514" class="t9" style="width:600px;">
                    <table class="t8" role="presentation" cellpadding="0" cellspacing="0" width="100%" style="width:100%;"><tr><td class="t7" style="border-bottom:1px solid #EFF1F4;padding:0 0 18px 0;"><h1 class="t6" style="margin:0;font-family:Helvetica,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:700;font-style:normal;font-size:22px;text-decoration:none;text-transform:none;letter-spacing:-1px;direction:ltr;color:#141414;text-align:left;">Ваш код подтверждения</h1></td></tr></table>
                </td></tr></table>
            </td></tr><tr><td><div class="t11" style="line-height:18px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
                <table class="t16" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;"><tr><td width="514" class="t15" style="width:600px;">
                    <table class="t14" role="presentation" cellpadding="0" cellspacing="0" width="100%" style="width:100%;"><tr><td class="t13"><p class="t12" style="margin:0;font-family:Helvetica,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:25px;font-weight:400;font-style:normal;font-size:15px;text-decoration:none;text-transform:none;letter-spacing:-0.1px;direction:ltr;color:#141414;text-align:left;">Вы указали этот адрес для регистрации на сайте оригинальных джерси: <a href="https://ballershop.ru" style="text-decoration: underline; color: #1c1c1c">ballershop.ru</a>. Для подтверждения укажите этот код на сайте:</p></td></tr></table>
                </td></tr></table>
            </td></tr><tr><td><div class="t17" style="line-height:15px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
                <table class="t22" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;"><tr><td width="514" class="t21" style="width:600px;">
                    <table class="t20" role="presentation" cellpadding="0" cellspacing="0" width="100%" style="width:100%;"><tr><td class="t19" style="padding:0 0 1px 0;"><h1 class="t18" style="margin:0;font-family:Montserrat,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:700;font-style:normal;font-size:26px;text-decoration:none;text-transform:none;letter-spacing:-1px;direction:ltr;color:#DC33DF;text-align:left;">${code.value}</h1></td></tr></table>
                </td></tr></table>
            </td></tr><tr><td><div class="t23" style="line-height:1px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td><div class="t32" style="mso-line-height-rule:exactly;mso-line-height-alt:40px;line-height:40px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
                <table class="t36" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;"><tr><td width="514" class="t35" style="width:600px;">
                    <table class="t34" role="presentation" cellpadding="0" cellspacing="0" width="100%" style="width:100%;"><tr><td class="t33" style="border-top:1px solid #DFE1E4;padding:24px 0 0 0;"><div class="t31" style="width:100%;text-align:left;"><div class="t30" style="display:inline-block;"><table class="t29" role="presentation" cellpadding="0" cellspacing="0" align="left" valign="top">
                        <tr class="t28"><td></td><td class="t27" valign="top">
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="t26" style="width:auto;"><tr><td class="t25" style="background-color:#FFFFFF;text-align:center;line-height:20px;"><span class="t24" style="display:block;margin:0;font-family:Helvetica,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:20px;font-weight:500;font-style:normal;font-size:12px;text-decoration:none;direction:ltr;color:#B4BECC;text-align:center;"><a href="https://ballershop.ru" style="color: #B4BECC !important">https://ballershop.ru</a></span></td></tr></table>
                        </td>
                            <td></td></tr>
                    </table></div></div></td></tr></table>
                </td></tr></table>
            </td></tr></table></td></tr></table>
        </td></tr></table>
    </td></tr><tr><td><div class="t42" style="line-height:50px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr></table></td></tr></table></div><div class="gmail-fix" style="display: none; white-space: nowrap; font: 15px courier; line-height: 0;"></div>
</body>
</html>`

        const response = await axios.post(process.env.VUE_APP_API_URL + "/mail/sendMsg", {
            to: emailValue.value,
            subject: 'Регистрация на сайте — Baller',
            html: html
        })

        if (response.status === 200) {
            registerStage.value = 2
        }
    }

    const registerUser = async () => {
        if (authType.value === 'login' ) return
        if (parseInt(codeValue.value) !== code.value) {
            notify('Ошибка: указан неверный код')
            return
        }

        const body = {
            firstname: firstnameValue.value,
            lastname: lastnameValue.value,
            email: emailValue.value,
            roleName: 'Пользователь',
            password: passwordValue.value,
            phone: phoneValue.value
        }

        try {
            const response = await axios.post(process.env.VUE_APP_API_URL + "/users/addUser", body)

            if (response?.data.type === 'successful') {
                notify(response.data.message)
                await initLogin()
            } else if (response?.data.type === 'error') {
                notify('Ошибка: ' + response.data.message)
            }
        } catch (error) {}
    }

    const saveToken = (token) => {
        setCookie('authToken', token, 7)
    }

    const setCookie = (cname, cvalue, exdays) => {
        const d = new Date()
        d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000))
        const expires = "expires=" + d.toUTCString()
        document.cookie = `${cname}=${cvalue};${expires};path=/`
        document.cookie = `${cname}=${encodeURIComponent(cvalue)};${expires};HttpOnly;Path=/;SameSite=Lax`
    }

    const authContainer = useTemplateRef('auth-container');
    const page = useTemplateRef('page');

    onMounted(async () => {
        authType.value = route.params.type

        try {
            await register()
        } catch (e) {}

        await nextTick(() => {
            if (authContainer.value && page.value) {
                authContainer.value.style.paddingTop = (page.value.clientHeight - parseInt(getComputedStyle(page.value).paddingTop) - parseInt(getComputedStyle(authContainer.value).height)) / 2 - 100 + 'px';
            }
        })
    })

    onUpdated( () => {
        authType.value = route.params.type
    })

    watch(
        () => route.params.type,
        (newVal, oldVal) => {
            authType.value = route.params.type
        },
    )
</script>

<template>
    <div class="silver-background" ref="page" @keyup.enter="registerUser">
        <ticker></ticker>
        <header-default></header-default>
        <div class="container page column-page">
            <div class="row justify-content-center">
                <div class="auth-container" ref="auth-container">

                    <div class="login-block" v-if="authType==='login'">
                        <h3>Авторизация</h3>
                        <div class="inputs-block">
                            <input id="email" type="text" placeholder="Ваш email" v-model="emailValue" name="email">
                            <p class="error-text" v-if="errorType === 'no-email'">Ошибка: {{ errorText }}</p>

                            <div class="password-container">
                                <input id="password" type="password" placeholder="Ваш пароль" v-model="passwordValue" name="password">
                                <div class="eye-icn"></div>
                            </div>
                            <p class="error-text" v-if="errorType !== 'no-email' && errorType"><span>Ошибка: {{ errorText }}. </span><router-link v-if="errorType === 'no-password'" to="/register"><span class="link">Регистрация</span></router-link></p>
                        </div>
                        <div class="send-btn btn" @click="initLogin">Отправить</div>
                    </div>
                    <div class="bottom-text" v-if="authType==='login'"><span>Нет аккаунта? —</span><router-link :to="{path: '/auth/register', params: {stage: 1}}"><span class="action-text">Регистрация</span></router-link></div>

                    <div class="register-block" v-if="authType==='register' && registerStage === 1">
                        <h3>Создать аккаунт</h3>
                        <div class="inputs-block">
                            <input v-model="firstnameValue" type="text" name="firstname" id="firstname" placeholder="Заполните ваше имя">
                            <p class="error-text" v-if="errorType === 'no-firstname'">Ошибка: {{ errorText }}</p>

                            <input v-model="lastnameValue" type="text" name="lastname" id="lastname" placeholder="Поле для фамилии">
                            <p class="error-text" v-if="errorType === 'no-lastname'">Ошибка: {{ errorText }}</p>

                            <input v-model="emailValue" type="text" name="email" id="email" placeholder="Ваш email">
                            <p class="error-text" v-if="errorType === 'no-email'">Ошибка: {{ errorText }}</p>

                            <input v-model="passwordValue" type="password" name="password" id="password" placeholder="Придумайте пароль хотя бы из 8 символов">
                            <p class="error-text" v-if="errorType === 'no-password'">Ошибка: {{ errorText }}</p>

                            <input v-model="phoneValue" type="text" name="phone" id="password" placeholder="Номер телефона (начните с «+»)">
                            <p class="error-text" v-if="errorType === 'no-phone'">Ошибка: {{ errorText }}</p>

                            <p class="error-text" v-if="errorType === 'valid-error'">Ошибка: {{ errorText }}</p>
                        </div>
                        <div class="send-btn btn" @click="sendConfirmCode">Отправить</div>
                    </div>
                    <div class="register-block" v-if="authType==='register' && registerStage === 2">
                        <h3>Подтверждение email</h3>
                        <div class="inputs-block">
                            <input v-model="codeValue" type="number" name="code" id="code" placeholder="Введите код">
                            <p class="error-text" v-if="errorType === 'no-code'">Ошибка: {{ errorText }}</p>
                        </div>
                        <div class="send-btn btn" @click="registerUser">Отправить</div>
                    </div>
                    <div class="bottom-text" v-if="authType==='register'"><span>Уже есть аккаунт? —</span><router-link to="/auth/login"><span class="action-text">Вход</span></router-link></div>

                </div>
            </div>
        </div>
    </div>
    <footer-default></footer-default>
</template>

<style scoped lang="scss">
@use '~@/assets/styles/vars' as *;
    .auth-container {
        display: flex;
        flex-direction: column;
        align-items: center;

        box-sizing: border-box;

        width: 40%;
    }

    .login-block, .register-block {
        background: $white;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 10% 8%;
        width: 100%;
        box-sizing: border-box;

        gap: 30px
    }

    .inputs-block {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 10px
    }

    input {
        border: 1.5px solid $hard-gray;
        border-radius: 3px;
        //text-transform: uppercase;
        //font-size: 11.5px;
        //font-weight: 500;
    }

    input:focus {
        border: 1.5px solid $black;
    }

    .send-btn {
        border-radius: 3px;
        background: $black;
        color: $white;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px 0;
    }

    h3 {
        font-size: 26px;
        color: $black;
        font-weight: 500;
    }

    .error-text {
        font-size: 12px;
        color: $purple
    }

    .error-text span.link {
        color: $purple;
        text-decoration: underline;
    }

    .password-container {
        position: relative;
    }

    .eye-icn {
        position: absolute;
        right: 0;
        top: 0;
        height: 80%;
        aspect-ratio: 1 / 1;
    }

    .bottom-text {
        margin-top: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px
    }

    .bottom-text .action-text {
        color: $purple;
        cursor: pointer;
    }

    .bottom-text .action-text:hover {
        text-decoration: underline;
    }

    @media screen and (max-width: 750px) {
        h3 {
            font-size: 22px;
        }

        .login-block {
            gap: 20px
        }

        .inputs-block input {
            font-size: 12px;
        }

        .auth-container {
            width: 100%;
        }

        .send-btn {
            font-size: 14px;
        }

        .bottom-text {
            font-size: 14px;
        }
    }
</style>